'use client';

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { marketplaceApi } from '@/lib/api';
import { MARKETPLACE_CONVERSATION_REFRESH_EVENT } from '@/lib/messaging-constants';
const POLL_INTERVAL_MS = 30_000;

let count = 0;
let subscribers = new Set<() => void>();
let polling = false;
let intervalId: ReturnType<typeof setInterval> | null = null;

function getSnapshot() {
  return count;
}

function subscribe(callback: () => void) {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

function setCount(next: number) {
  if (next === count) return;
  count = next;
  subscribers.forEach((cb) => cb());
}

async function fetchCount() {
  try {
    const res = await marketplaceApi.getUnreadMessageCount();
    setCount(res.count);
  } catch {
    setCount(0);
  }
}

function startPolling() {
  if (polling) return;
  polling = true;
  void fetchCount();
  intervalId = setInterval(() => void fetchCount(), POLL_INTERVAL_MS);

  const onConversationUpdated = () => void fetchCount();
  window.addEventListener(MARKETPLACE_CONVERSATION_REFRESH_EVENT, onConversationUpdated);

  const originalStop = stopPolling;
  stopPolling = () => {
    window.removeEventListener(MARKETPLACE_CONVERSATION_REFRESH_EVENT, onConversationUpdated);
    originalStop();
  };
}

let stopPolling = () => {
  if (!polling) return;
  polling = false;
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

let activeConsumers = 0;

/**
 * Shared hook for unread message count.
 * Polls the API at a fixed interval and listens for conversation refresh events.
 * Multiple consumers share a single polling loop.
 */
export function useUnreadMessages(isLoggedIn: boolean) {
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn) {
      setCount(0);
      return;
    }
    activeConsumers++;
    if (!mountedRef.current) {
      mountedRef.current = true;
      startPolling();
    }
    return () => {
      activeConsumers--;
      if (activeConsumers <= 0) {
        activeConsumers = 0;
        stopPolling();
      }
    };
  }, [isLoggedIn]);

  const refresh = useCallback(() => {
    void fetchCount();
  }, []);

  const unreadCount = useSyncExternalStore(subscribe, getSnapshot, () => 0);

  return { unreadCount, refresh };
}
